import {
  GraphQLClientError,
  resubscribe,
  ResubscribeOptions,
  Subscription,
} from '@avantstay/graphql-ts-client/browser'

interface SharedSubscriptionConfig<Args, Value> extends Omit<
  ResubscribeOptions,
  'signal' | 'onStateChange'
> {
  getKey: (args: Args) => string
  open: (args: Args) => Subscription<Value>
}

interface SubscribeParams<Args, Value> {
  args: Args
  signal: AbortSignal
  onValue: (value: Value) => void
  onLive?: (isLive: boolean) => void
}

interface Connection<Args, Value> {
  controller: AbortController
  listeners: Set<SubscribeParams<Args, Value>>
  isLive: boolean
}

/**
 * The server answered and refused: a 4xx, or a 200 whose event carries errors, as for a user
 * without the privileges. Every retry would be refused again.
 */
const isRefused = (error: unknown) => {
  if (!(error instanceof GraphQLClientError)) return false

  const { status } = error.responseData

  return status !== undefined && status < 500
}

/**
 * Shares one resubscribing connection per key between every subscriber, until the last one
 * aborts. Connections reconnect with backoff after a drop or a server completion, and stop for
 * good when the server refuses them, so the next subscriber asks again.
 */
export const createSharedSubscription = <Args, Value>({
  getKey,
  open,
  ...options
}: SharedSubscriptionConfig<Args, Value>) => {
  const connections = new Map<string, Connection<Args, Value>>()

  const openConnection = (key: string, args: Args) => {
    const connection: Connection<Args, Value> = {
      controller: new AbortController(),
      listeners: new Set(),
      isLive: false,
    }

    resubscribe(() => open(args), {
      signal: connection.controller.signal,
      shouldRetry: error => !isRefused(error),
      onStateChange: ({ state }) => {
        connection.isLive = state === 'live'
        connection.listeners.forEach(listener => listener.onLive?.(connection.isLive))
      },
      ...options,
    }).subscribe({
      next: value => connection.listeners.forEach(listener => listener.onValue(value)),
      error: () => {
        if (connections.get(key) === connection) {
          connections.delete(key)
        }
      },
    })

    return connection
  }

  const subscribe = (params: SubscribeParams<Args, Value>) => {
    const { args, signal } = params

    if (signal.aborted) return

    const key = getKey(args)
    const connection = connections.get(key) ?? openConnection(key, args)

    connections.set(key, connection)
    connection.listeners.add(params)
    params.onLive?.(connection.isLive)

    signal.addEventListener(
      'abort',
      () => {
        connection.listeners.delete(params)

        if (connection.listeners.size) return

        connection.controller.abort()

        if (connections.get(key) === connection) connections.delete(key)
      },
      { once: true },
    )
  }

  const isLive = (key: string) => {
    return !!connections.get(key)?.isLive
  }

  return { subscribe, isLive }
}
